import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { getToken } from '../utils/auth';

const initialState = {
  title: '',
  type: 'Event',
  shortDesc: '',
  detailedDesc: '',
  date: '',
  image: null,
};

const AdminNewsEventForm = ({ editingItem, onItemAdded }) => {
  const [form, setForm] = useState(initialState);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (editingItem) {
      setForm({
        ...editingItem,
        image: null, // Don't prefill file input
      });
    } else {
      setForm(initialState);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }, [editingItem]);

  const handleChange = (e) => {
    const { name, value, files } = e.target;
    if (name === 'image') {
      setForm((prev) => ({ ...prev, image: files[0] }));
    } else {
      setForm((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    try {
      const formData = new FormData();
      formData.append('title', form.title);
      formData.append('type', form.type);
      formData.append('shortDesc', form.shortDesc);
      formData.append('detailedDesc', form.detailedDesc);
      formData.append('date', form.date);
      if (form.image) formData.append('image', form.image);

      const config = {
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: `Bearer ${getToken()}`,
        },
      };

      if (editingItem && editingItem.id) {
        // Update
        await axios.put(
          `${process.env.REACT_APP_API_URL}/api/news-events/${editingItem.id}`,
          formData,
          config
        );
      } else {
        // Create
        await axios.post(
          `${process.env.REACT_APP_API_URL}/api/news-events`,
          formData,
          config
        );
      }
      setForm(initialState);
      if (fileInputRef.current) fileInputRef.current.value = '';
      if (onItemAdded) onItemAdded();
    } catch (err) {
      setError(
        err.response?.data?.message || 'Failed to submit. Please try again.'
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="card mb-4 shadow-sm">
      <div className="card-body">
        <h5 className="card-title mb-3">
          {editingItem ? 'Edit Event/News' : 'Add New Event/News'}
        </h5>
        {error && <div className="alert alert-danger">{error}</div>}
        <form onSubmit={handleSubmit}>
          <div className="row g-3">
            <div className="col-md-6">
              <label className="form-label">Title</label>
              <input
                type="text"
                className="form-control"
                name="title"
                value={form.title}
                onChange={handleChange}
                required
              />
            </div>
            <div className="col-md-3">
              <label className="form-label">Type</label>
              <select
                className="form-select"
                name="type"
                value={form.type}
                onChange={handleChange}
                required
              >
                <option value="Event">Event</option>
                <option value="News">News</option>
              </select>
            </div>
            <div className="col-md-3">
              <label className="form-label">Date</label>
              <input
                type="date"
                className="form-control"
                name="date"
                value={form.date}
                onChange={handleChange}
                required
              />
            </div>
            <div className="col-12">
              <label className="form-label">Short Description</label>
              <input
                type="text"
                className="form-control"
                name="shortDesc"
                value={form.shortDesc}
                onChange={handleChange}
                required
                maxLength={150}
              />
            </div>
            <div className="col-12">
              <label className="form-label">Detailed Description</label>
              <textarea
                className="form-control"
                name="detailedDesc"
                value={form.detailedDesc}
                onChange={handleChange}
                rows={4}
                required
              />
            </div>
            <div className="col-12 col-md-6">
              <label className="form-label">Image {editingItem && editingItem.image && (
                <span className="ms-2 small text-muted">(Leave empty to keep current)</span>
              )}</label>
              <input
                type="file"
                className="form-control"
                name="image"
                accept="image/*"
                ref={fileInputRef}
                onChange={handleChange}
              />
              {editingItem && editingItem.image && (
                <div className="mt-2">
                  <img
                    src={`${process.env.REACT_APP_API_URL}/uploads/${editingItem.image}`}
                    alt="Current"
                    style={{ maxHeight: '80px', borderRadius: '6px' }}
                  />
                </div>
              )}
            </div>
          </div>
          <div className="mt-4">
            <button
              type="submit"
              className="btn btn-primary px-4"
              disabled={submitting}
            >
              {submitting ? 'Saving...' : editingItem ? 'Update' : 'Add'}
            </button>
            {editingItem && (
              <button
                type="button"
                className="btn btn-secondary ms-2"
                onClick={() => {
                  setForm(initialState);
                  if (fileInputRef.current) fileInputRef.current.value = '';
                  if (onItemAdded) onItemAdded();
                }}
              >
                Cancel
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default AdminNewsEventForm;
